import { apiRequest } from "./client";
import { z } from "zod";
import type {
  BulkConfirmation,
  BulkReviewStatus,
  BulkTransactionItem,
  BulkTransactionRecord,
  BulkTransferValidationSummary,
  Channel,
  ReportFormat,
  UploadedBulkTransfer,
} from "@kkp/api-client";

import { parseListItems, wireInt } from "./parse";

export type {
  BulkTransactionItem,
  BulkTransactionRecord,
  BulkTransferValidationItem,
  BulkTransferValidationSummary,
} from "@kkp/api-client";

// Boundary schemas. Money fields are validated as strings: the BFFE serializes
// MoneyIdr as a string and the FE keeps money as an opaque string end-to-end.
export const BulkTransactionRecordSchema = z.object({
  seqId: wireInt,
  bulkTransactionId: z.string(),
  createdAt: z.string(),
  executedAt: z.string(),
  totalReceivable: z.string(),
  totalToFund: z.string(),
  totalRecipients: wireInt,
  status: z.enum(["Pending", "Executed", "Failed"]),
  channel: z.enum(["VA", "Bank"]),
  confirmedByClientAt: z.string().optional(),
});

export const BulkTransactionItemSchema = z.object({
  fullName: z.string(),
  employeeId: z.string(),
  bankCode: z.string(),
  bankAccount: z.string(),
  receivable: z.string(),
  status: z.enum(["Success", "Failed", "Review"]),
  refundedAt: z.string().optional(),
  refundReference: z.string().optional(),
});

export async function listBulkTransfers(): Promise<BulkTransactionRecord[]> {
  const data = await apiRequest<unknown>({
    path: "/bulk-transfers",
    method: "GET",
  });

  return parseListItems(BulkTransactionRecordSchema, data, "bulk transfers");
}

export async function downloadBulkTemplate(): Promise<string> {
  return apiRequest<string>({
    path: "/bulk-transfers/template",
    method: "GET",
  });
}

/** A retry with the same `idempotencyKey` returns the batch already created. */
export async function uploadBulkTransfer(
  channel: Channel,
  file: File,
  idempotencyKey: string,
): Promise<UploadedBulkTransfer> {
  const body = new FormData();
  body.append("file", file);

  return apiRequest<UploadedBulkTransfer, FormData>({
    path: `/bulk-transfers?channel=${encodeURIComponent(channel)}`,
    method: "POST",
    body,
    headers: { "Idempotency-Key": idempotencyKey },
  });
}

export async function validateBulkTransfer(
  channel: Channel,
  file: File,
): Promise<BulkTransferValidationSummary> {
  const body = new FormData();
  body.append("file", file);

  return apiRequest<BulkTransferValidationSummary, FormData>({
    path: `/bulk-transfers/validate?channel=${encodeURIComponent(channel)}`,
    method: "POST",
    body,
  });
}

export async function listBulkTransferItems(
  bulkTransactionId: string,
): Promise<BulkTransactionItem[]> {
  const data = await apiRequest<unknown>({
    path: `/bulk-transfers/${bulkTransactionId}/items`,
    method: "GET",
  });

  return parseListItems(BulkTransactionItemSchema, data, "bulk transfer items");
}

export async function downloadBulkReport(
  bulkTransactionId: string,
  format: ReportFormat = "csv",
): Promise<string | Blob> {
  return apiRequest<string | Blob>({
    path: `/bulk-transfers/${bulkTransactionId}/report?format=${format}`,
    method: "GET",
  });
}

export function getBulkReviewStatus(
  bulkTransactionId: string,
): Promise<BulkReviewStatus> {
  return apiRequest<BulkReviewStatus>({
    path: `/bulk-transfers/${bulkTransactionId}/review`,
    method: "GET",
  });
}

export async function confirmBulkTransfer(
  bulkTransactionId: string,
): Promise<BulkConfirmation> {
  return apiRequest<BulkConfirmation>({
    path: `/bulk-transfers/${encodeURIComponent(bulkTransactionId)}/confirm`,
    method: "POST",
    body: {},
  });
}
